import {type DefaultError, useMutation} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {environment} from "~/env";
import {notificationStore} from "~/stores/init";
import {CHAT_CONFIG} from "~/pages/chat-agent/services/ChatConfiguration";
import {myDashboardUrl} from "./dashboardsApi";

type DashboardMutationResponse = {message: string};

export type DeleteDashboardVariables = {dashboardId: number};
export type RenameDashboardVariables = {dashboardId: number; title: string};

function assertApiConfigured() {
    if (!CHAT_CONFIG.API_URL) {
        throw new Error("Chat API URL is not configured");
    }
}

/**
 * `DELETE /superset/dashboards/{dashboard_id}`: removes the dashboard from the user's list only; the
 * Superset dashboard itself is untouched.
 */
export function useDeleteDashboardMutation() {
    return useMutation<DashboardMutationResponse, DefaultError, DeleteDashboardVariables>({
        mutationFn: async ({dashboardId}) => {
            if (environment.isTestChat) {
                return {message: "Test mode: delete dashboard skipped"};
            }
            assertApiConfigured();
            const {data} = await queryHttp.request<DashboardMutationResponse>({
                url: myDashboardUrl(dashboardId),
                method: "DELETE",
            });
            return data;
        },
    });
}

/** `PATCH /superset/dashboards/{dashboard_id}/rename` with `{title}`. */
export function useRenameDashboardMutation() {
    return useMutation<DashboardMutationResponse, DefaultError, RenameDashboardVariables>({
        mutationFn: async ({dashboardId, title}) => {
            if (environment.isTestChat) {
                return {message: "Test mode: rename dashboard skipped"};
            }
            assertApiConfigured();
            const {data} = await queryHttp.request<DashboardMutationResponse>({
                url: `${myDashboardUrl(dashboardId)}/rename`,
                method: "PATCH",
                data: {title},
            });
            return data;
        },
    });
}

/** "Could not delete the dashboard. Please try again." and the like, one toast per dashboard. */
export function notifyDashboardError(action: "delete" | "rename", dashboardId: string) {
    notificationStore.show(`Could not ${action} the dashboard. Please try again.`, {
        severity: "error",
        id: `dsh-${action}-error-${dashboardId}`,
        autoHideDuration: 4000,
    });
}
